import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { VoteStatus } from "types/voteEnum";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import groupAndSortElectionProposals from "../services/groupAndSortElectionProposals";

import BasicCaseProposal from "components/vote/Votebox/common/Buttons/BasicCaseProposal";
import ShowAllOptionsButton from "components/vote/Votebox/common/Buttons/ShowAllOptionsButton";
import ActionSection from "components/vote/Votebox/common/ActionSection";
import VoteGroup from "components/vote/Votebox/common/VoteGroup";
import {
    StyledNumberOfVoices,
    StyledSortSelection,
    ToolbarRow,
    Wrapper,
} from "./VoteBasic.styles";

type Proposal = {
    disableOtherPropositionsIfSelected: boolean;
    id: string;
    text: string;
    isVisibleByDefault: boolean;
    description: string;
    image: string;
};

type Props = {
    propositions: Array<Proposal>;
    answers: Array<{
        id: string;
        weight: number;
        disableOtherPropositionsIfSelected: boolean;
    }>;
    isSecret: boolean;
    voterWeight: number;
    voteStatus: string;
    maxNumberOfAnswers: number;
    selectProposal: (propositionId: string) => void;
    unselectProposal: (propositionId: string) => void;
    validateVote: () => void;
    resetVote: () => void;
    canAnswerBeUpdated: boolean;
    titleId: string;
    // Set when the user votes for several voters, to name each group after its voter.
    groupVoterName?: string;
};

const VoteBasic = ({
    propositions,
    answers,
    isSecret,
    voterWeight,
    voteStatus,
    maxNumberOfAnswers,
    selectProposal,
    unselectProposal,
    validateVote,
    resetVote,
    canAnswerBeUpdated,
    titleId,
    groupVoterName,
}: Props) => {
    const [shouldShowHiddenProposals, setShouldShowHiddenProposals] =
        useState(false);
    // Proposals selected when the sort was asked, null when the sort is off.
    const [sortedProposalIds, setSortedProposalIds] =
        useState<Array<string> | null>(null);
    const hiddenProposalsId = useId();

    const { t } = useTranslation();

    const selectedAnswerIds = answers
        .filter((answer) => answer.weight > 0)
        .map((answer) => answer.id);
    const isSortActive = null !== sortedProposalIds;

    // The sort freezes the current selection: the list does not move under the focus.
    function handleSortClick() {
        setSortedProposalIds(isSortActive ? null : selectedAnswerIds);
    }

    function renderSortButton() {
        if (maxNumberOfAnswers <= 1) {
            return null;
        }

        return (
            <StyledSortSelection
                type="button"
                aria-pressed={isSortActive}
                $isPressed={isSortActive}
                onClick={handleSortClick}
            >
                <ExpandLessIcon aria-hidden="true" />
                {t("sortSelection")}
                {` (${selectedAnswerIds.length}/${maxNumberOfAnswers})`}
            </StyledSortSelection>
        );
    }

    function renderToggleHiddenProposalsButton(hiddenCount: number) {
        /* If one proposal is hidden, display the toggle  */
        return hiddenCount > 0 ? (
            <ShowAllOptionsButton
                controlsId={hiddenProposalsId}
                handleToggle={() =>
                    setShouldShowHiddenProposals(!shouldShowHiddenProposals)
                }
                hiddenCount={hiddenCount}
                isExpanded={shouldShowHiddenProposals}
            />
        ) : null;
    }

    const { visibleProposals, hiddenProposals } = groupAndSortElectionProposals(
        propositions.map((prop) => {
            const isActive =
                VoteStatus.Success === voteStatus && isSecret
                    ? false
                    : answers.some(
                          (answer) =>
                              answer.id === prop.id && answer.weight > 0,
                      );

            return {
                ...prop,
                isActive,
            };
        }),
        {
            sortingIsEnabled: isSortActive,
            isSorted: (proposal) =>
                Boolean(sortedProposalIds?.includes(proposal.id)),
        },
    );

    // As soon as one candidate has a photo or a description, every text starts at the same place.
    const hasRichProposals = propositions.some(
        (proposition) => proposition.image || proposition.description,
    );

    function renderProposals(
        proposals: Array<Proposal & { isActive: boolean }>,
    ) {
        const activeUniqueAnswerId = answers
            .filter((answer) => answer.disableOtherPropositionsIfSelected)
            .find((answer) => answer.weight > 0);

        const isUniqueAnswerConstraintActive = Boolean(activeUniqueAnswerId);

        return proposals.map((proposition) => {
            const isDisabled = () => {
                if (VoteStatus.Success === voteStatus) {
                    return true;
                }

                if (isUniqueAnswerConstraintActive) {
                    return activeUniqueAnswerId?.id !== proposition.id;
                }

                if (proposition.isActive) {
                    return false;
                }

                return selectedAnswerIds.length === maxNumberOfAnswers;
            };

            const handleProposalClick = () => {
                if (proposition.isActive) {
                    unselectProposal(proposition.id);
                    return;
                }

                selectProposal(proposition.id);
            };

            return (
                <BasicCaseProposal
                    text={proposition.text}
                    isActive={proposition.isActive}
                    isDisabled={isDisabled()}
                    isTextAlignedStart={hasRichProposals}
                    handleClick={handleProposalClick}
                    key={proposition.id}
                    image={proposition.image}
                    description={proposition.description}
                />
            );
        });
    }

    return (
        <Wrapper>
            <ToolbarRow>
                {voterWeight > 1 ? (
                    <StyledNumberOfVoices>
                        {t("numberOfVote")} {voterWeight}
                    </StyledNumberOfVoices>
                ) : null}
                {renderSortButton()}
            </ToolbarRow>
            <VoteGroup titleId={titleId} voterName={groupVoterName}>
                {renderProposals(visibleProposals)}
                {renderToggleHiddenProposalsButton(hiddenProposals.length)}
                <div id={hiddenProposalsId}>
                    {shouldShowHiddenProposals
                        ? renderProposals(hiddenProposals)
                        : null}
                </div>
            </VoteGroup>
            <ActionSection
                validateVote={validateVote}
                resetVote={resetVote}
                voteStatus={voteStatus as VoteStatus}
                canAnswerBeUpdated={canAnswerBeUpdated}
            />
        </Wrapper>
    );
};

export default VoteBasic;
